import { useFormik } from 'formik'
import React, { useState } from 'react'
import {
  ButtonType,
  MaskType,
  ModalSize,
  showAlert,
  showError,
  toAbsoluteUrl,
  IconPaths,
} from '../../../../../../_metronic/helpers'
import { useAuth } from '../../../../auth'
import { temVinculoMedicoAtivo } from 'app/modules/auth/core/vinculos'
import { persistCertificado } from '../../../../auth/core/_requests'
import {
  Button,
  FormDateInput,
  FormInput,
  Label,
  LoadingIndicator,
  useModal,
} from '../../../../../components'
import { profileSchema } from './validation/profileSchema'
import { UploadAvatarModal } from '../../modal/UploadAvatarModal'
import { UsuarioDtoUpdate } from 'app/modules/apps/admin/usuarios/usuarios-list/core/_models'
import { updateUserAutenticado } from 'app/modules/apps/admin/usuarios/usuarios-list/core/_requests'

export const ProfileDetails: React.FC = () => {
  const { reloadUser, currentUser } = useAuth()
  const { showModal } = useModal()

  const [certificadoValue, setCertificadoValue] = useState<File | undefined>()
  const [showPassword, setShowPassword] = useState(false)

  const isMedico = temVinculoMedicoAtivo(currentUser?.vinculos)
  const possuiCertificado = currentUser?.pessoa?.certificadoDigital

  const initialValues = {
    id: Number(currentUser?.id),
    email: String(currentUser?.email),
    password: '',
    pessoa: {
      id: Number(currentUser?.pessoa?.id),
      primeiroNome: String(currentUser?.pessoa?.primeiroNome),
      sobreNome: String(currentUser?.pessoa?.sobreNome),
      cpf: String(currentUser?.pessoa?.cpf),
      dataNascimento: currentUser?.pessoa?.dataNascimento
        ? currentUser?.pessoa?.dataNascimento
        : '',
      telefone: currentUser?.pessoa?.telefone,
    },
  }

  const formik = useFormik({
    initialValues,
    validationSchema: profileSchema(showPassword, isMedico),
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true)
      try {
        const usuario: UsuarioDtoUpdate = {
          id: values.id,
          primeiroNome: values.pessoa.primeiroNome,
          sobreNome: values.pessoa.sobreNome,
          email: values.email,
          nascimento: values.pessoa.dataNascimento,
          telefone: values.pessoa.telefone || undefined,
        }
        await updateUserAutenticado(usuario)

        if (isMedico && certificadoValue)
          await persistCertificado(values.password, certificadoValue)

        await reloadUser()
        showAlert({ icon: 'success', title: 'Sucesso', text: 'Perfil atualizado com sucesso!' })
      } catch (error) {
        showError(error)
      } finally {
        setSubmitting(false)
      }
    },
  })

  const showAvatarModal = () =>
    showModal({
      title: 'Avatar',
      content: <UploadAvatarModal />,
      size: ModalSize.Small,
    })

  return (
    <div id='msgUser' className='card mb-5 mb-xl-10'>
      <div
        className='card-header border-0 cursor-pointer'
        role='button'
        data-bs-toggle='collapse'
        data-bs-target='#kt_account_profile_details'
        aria-expanded='true'
        aria-controls='kt_account_profile_details'
      >
        <div className='card-title m-0'>
          <h3 className='fw-bolder m-0'>Detalhes do Perfil</h3>
        </div>
      </div>
      <div id='kt_account_profile_details' className='collapse show'>
        <form onSubmit={formik.handleSubmit} noValidate className='form'>
          <div className='card-body'>
            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label fw-bold fs-6'>Avatar</label>
              <div className='col-lg-8'>
                <div className='image-input' data-kt-image-input='true'>
                  <div
                    className='image-input-wrapper w-125px h-125px position-relative'
                    style={{
                      backgroundImage: `url(${toAbsoluteUrl(String(currentUser?.imagemPerfil))})`,
                    }}
                  >
                    <Button
                      className='btn btn-icon btn-light shadow'
                      icon={IconPaths.Pen}
                      ariaLabel='Alterar foto de perfil'
                      onClick={showAvatarModal}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className='row mb-6'>
              <Label className='col-lg-4 col-form-label' isRequired>
                Primeiro Nome
              </Label>
              <div className='col-lg-8 fv-row'>
                <FormInput formik={formik} name='pessoa.primeiroNome' placeholder='Primeiro Nome' />
              </div>
            </div>
            <div className='row mb-6'>
              <Label className='col-lg-4 col-form-label' isRequired>
                Sobrenome
              </Label>
              <div className='col-lg-8 fv-row'>
                <FormInput formik={formik} name='pessoa.sobreNome' placeholder='Sobrenome' />
              </div>
            </div>
            <div className='row mb-6'>
              <Label className='col-lg-4 col-form-label' isRequired>
                E-mail
              </Label>
              <div className='col-lg-8 fv-row'>
                <FormInput formik={formik} name='email' placeholder='E-mail' />
              </div>
            </div>

            <div className='row mb-6'>
              <Label className='col-lg-4 col-form-label' isRequired>
                Data de Nascimento
              </Label>
              <div className='col-lg-8 fv-row'>
                <FormDateInput
                  formik={formik}
                  name='pessoa.dataNascimento'
                  placeholder='Data de Nascimento'
                />
              </div>
            </div>
            <div className='row mb-6'>
              <Label className='col-lg-4 col-form-label'>Telefone</Label>
              <div className='col-lg-8 fv-row'>
                <FormInput
                  formik={formik}
                  name='pessoa.telefone'
                  placeholder='Telefone'
                  mask={MaskType.Phone}
                />
              </div>
            </div>
            {isMedico && (
              <>
                <div className='row mb-6'>
                  <label className='col-lg-4 col-form-label fw-bold fs-6'>
                    <span className='required me-2'>Certificado</span>
                    <span
                      className={possuiCertificado ? 'badge badge-success' : 'badge badge-danger'}
                    >
                      {possuiCertificado ? 'Médico com certificado' : 'Médico sem certificado'}
                    </span>
                  </label>
                  <div className='col-lg-8 fv-row'>
                    <input
                      name='file'
                      type='file'
                      onChange={(event) => {
                        setCertificadoValue(
                          event.currentTarget.files !== null
                            ? event.currentTarget.files[0]
                            : undefined
                        )
                        setShowPassword(true)
                      }}
                      className='form-control'
                      disabled={formik.isSubmitting}
                    />
                  </div>
                </div>
                {showPassword && (
                  <div className='row mb-6'>
                    <Label className='col-lg-4 col-form-label' isRequired>
                      Senha
                    </Label>
                    <div className='col-lg-8 fv-row'>
                      <FormInput
                        formik={formik}
                        type='password'
                        name='password'
                        placeholder='Senha do certificado'
                      />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          <div className='card-footer d-flex justify-content-end py-6 px-9'>
            <Button type={ButtonType.Submit} className='btn-primary' disabled={formik.isSubmitting}>
              {formik.isSubmitting ? <LoadingIndicator /> : 'Salvar Alterações'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
